import { ACTIVE_SERVICE_TYPES } from "@shared/pre-analise/types";

type DossierLike = {
  id: string;
  status: string;
  numeroBat: string | null;
  veiculoId: string | null;
  solicitanteId: string;
  numeroSinistro?: string | null;
  seguradoNome?: string | null;
  eventoEndereco?: string | null;
};

type VehicleLike = {
  id: string;
  parceiroId: string | null;
  placa: string | null;
  marca: string | null;
  modelo: string | null;
  ativo: boolean;
  sistema: string;
};

export function vehiclesForRequester<T extends VehicleLike>(vehicles: T[], requesterId?: string): T[] {
  return requesterId
    ? vehicles.filter((vehicle) => vehicle.parceiroId === requesterId && vehicle.ativo && vehicle.sistema === "remonta")
    : [];
}

export function filterDossiers<T extends DossierLike>(
  dossiers: T[], vehicles: VehicleLike[], status: string, search: string,
): T[] {
  const term = search.trim().toLocaleLowerCase("pt-BR");
  const byId = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]));
  return dossiers.filter((dossier) => {
    if (status && dossier.status !== status) return false;
    if (!term) return true;
    const vehicle = dossier.veiculoId ? byId.get(dossier.veiculoId) : undefined;
    return [dossier.numeroSinistro, dossier.numeroBat, dossier.seguradoNome, dossier.eventoEndereco, vehicle?.placa, vehicle?.marca, vehicle?.modelo]
      .filter(Boolean).some((value) => String(value).toLocaleLowerCase("pt-BR").includes(term));
  });
}

export type { DossierIntakeInput } from "@shared/routes";

export function validateVehicleFields(input: {
  objetoTipo?: "veiculo" | "imovel" | "outro";
  veiculoId?: string | null;
  classificacaoOriginal?: string | null;
  ufRegistro?: string | null;
}): Partial<Record<"veiculoId" | "classificacaoOriginal" | "ufRegistro", string>> {
  const errors: Partial<Record<"veiculoId" | "classificacaoOriginal" | "ufRegistro", string>> = {};
  if (input.objetoTipo !== "veiculo" && !(input.objetoTipo == null && input.veiculoId)) return errors;
  if (!input.veiculoId) errors.veiculoId = "Selecione o veiculo";
  if (!["pequena_monta", "media_monta", "grande_monta"].includes(input.classificacaoOriginal ?? "")) errors.classificacaoOriginal = "Informe a classificacao original";
  if (!/^[A-Z]{2}$/.test(input.ufRegistro ?? "")) errors.ufRegistro = "Informe a UF de registro";
  return errors;
}

export function dossierDisplayState(
  dossier: { id: string; arquivadoEm?: Date | string | null },
  orders: readonly { dossieId: string; status: string }[],
): string {
  if (dossier.arquivadoEm) return "arquivado";
  return orders.find((order) => order.dossieId === dossier.id && !["concluido", "cancelado"].includes(order.status))?.status ?? "disponivel";
}

/** Mesma ordem e regra de server/remonta/ordem-servico.ts (assertCanCreateOrder). */
const SERVICE_ORDER = ACTIVE_SERVICE_TYPES;
type ServiceTypeLike = (typeof SERVICE_ORDER)[number];
type OrderLike = { tipo: string; status: string };
const isActiveOrder = (order: OrderLike) => !["concluido", "cancelado"].includes(order.status);

/** Modalidades que ainda podem ser solicitadas: nenhuma se houver Ordem ativa; nunca repete modalidade (mesmo cancelada). */
export function availableServiceTypes(orders: readonly OrderLike[], options: { hasVehicleData?: boolean } = {}): ServiceTypeLike[] {
  if (orders.some(isActiveOrder)) return [];
  const used = new Set(orders.map((order) => order.tipo));
  return SERVICE_ORDER.filter((tipo) => !used.has(tipo) && (tipo !== "reclassificacao_monta" || options.hasVehicleData !== false));
}

/** Explicação para o botão desabilitado; null quando é possível adicionar serviço. */
export function addServiceBlockReason(
  dossier: { arquivadoEm?: Date | string | null },
  orders: readonly OrderLike[],
  options: { hasVehicleData?: boolean } = {},
): string | null {
  if (dossier.arquivadoEm) return "Dossiê arquivado: reabra para solicitar novo serviço.";
  if (orders.some(isActiveOrder)) return "Há um serviço em andamento. Conclua ou cancele antes de solicitar outro.";
  if (!availableServiceTypes(orders, options).length) return "Todas as modalidades disponíveis já foram solicitadas neste Dossiê.";
  return null;
}
